
import React, { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { Edit, Trash2, Plus, ArrowLeft } from 'lucide-react'
import TeamForm from './TeamForm'

interface TeamMember {
  id: string
  name: string
  position: string
  bio: string | null
  image_url: string | null
  email: string | null
  linkedin_url: string | null
  display_order: number
  is_active: boolean
}

const TeamManager = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [editingTeamMember, setEditingTeamMember] = useState<TeamMember | null>(null)
  const [showForm, setShowForm] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    fetchTeamMembers()
  }, [])

  const fetchTeamMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .order('display_order')
      
      if (error) throw error
      setTeamMembers(data || [])
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch team members",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this team member?')) return

    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      
      toast({
        title: "Success",
        description: "Team member deleted successfully",
      })
      
      fetchTeamMembers()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete team member",
        variant: "destructive",
      })
    }
  }

  const handleEdit = (teamMember: TeamMember) => {
    setEditingTeamMember(teamMember)
    setShowForm(true)
  }

  const handleAdd = () => {
    setEditingTeamMember(null)
    setShowForm(true)
  }

  const handleFormSave = () => {
    setShowForm(false)
    setEditingTeamMember(null)
    fetchTeamMembers()
  }

  const handleFormCancel = () => {
    setShowForm(false)
    setEditingTeamMember(null)
  }

  if (loading) {
    return <div>Loading team members...</div>
  }

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleFormCancel}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Team
          </Button>
          <h2 className="text-2xl font-bold">
            {editingTeamMember ? 'Edit Team Member' : 'Add New Team Member'}
          </h2>
        </div>
        <TeamForm
          teamMember={editingTeamMember}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Manage Team</h2>
        <Button onClick={handleAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Team Members List</CardTitle>
          <CardDescription>Manage team member information</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">{member.name}</TableCell>
                  <TableCell>{member.position}</TableCell>
                  <TableCell>{member.email || '-'}</TableCell>
                  <TableCell>{member.display_order}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      member.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {member.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEdit(member)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDelete(member.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

export default TeamManager
